"use client"

import * as React from "react"

import { useFinanceFeatureQuery } from "@/features/finance/services/finance-query"
import { FinanceOverviewChartId } from "@/features/finance/model/finance-normalized-overview"
import { findChartById } from "@/features/finance/lib/finance-overview-integration"
import type { FinanceOverviewQuery } from "@/features/finance/model/finance-types"
import {
  useFinanceOverviewPeriod,
  useFinanceOverviewScope,
} from "@/features/finance/overview/context/finance-overview-context"

export interface ChurchActivityRow {
  name: string
  net: string
  netIsNegative: boolean
}

/**
 * Net per activity across every branch, from the consolidated overview's activity chart. One hook so the older
 * activity widget and the F3 tile read the SAME rows (and React Query makes one request for both).
 */
export function useChurchActivityRows(
  /** Another period than the board's (the F3 previous-period fallback), and whether to ask at all. */
  options: { period?: { from: string; to: string }; enabled?: boolean } = {},
) {
  const scope = useFinanceOverviewScope()
  const boardPeriod = useFinanceOverviewPeriod()
  const period = options.period ?? boardPeriod
  const { useFinanceOverviewQuery } = useFinanceFeatureQuery()

  const query = useFinanceOverviewQuery(
    (options.enabled ?? true) && scope.workspaceId && period.from && period.to
      ? ({
          workspaceId: scope.workspaceId,
          context: scope.overviewContext,
          dateFrom: period.from,
          dateTo: period.to,
          // No `profile`: this IS a breakdown, and the dashboard profile trims those.
        } satisfies FinanceOverviewQuery)
      : null,
  )

  const data = React.useMemo(() => {
    if (!query.data) return undefined
    const chart = findChartById(query.data, FinanceOverviewChartId.FinanceByActivity)
    const labels = chart?.labels ?? []
    const values = chart?.series?.[0]?.data ?? []

    const rows: ChurchActivityRow[] = labels.map((label, index) => {
      const net = String(values[index] ?? "0")
      return { name: label, net, netIsNegative: net.trim().startsWith("-") }
    })

    return { rows, currency: query.data.meta?.currency ?? scope.currency }
  }, [query.data, scope.currency])

  return { data, query }
}
